import { Typography } from '@mui/material';
import { TooltipContentType } from 'enums';
import { GrammarReplacement, HighlightTexts, HighlightType, Paraphrase } from 'features/augmented-writing/augmented-writing.types';
import { HighlightBlot } from 'features/augmented-writing/editor/highlight-blot';
import _ from 'lodash';
import Logger from 'logger';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import useOutlookTextStore from 'stores/outlook-text-store';
import { makeStyles } from 'tss-react/mui';
import PaiCircularProgress from '../../ui/base-components/pai-circular-progress';
import PaiDotLoader from '../../ui/base-components/pai-dot-loader';
import EditorStyles from './editor-styles';
import HighlightTooltip from './highlight-tooltip';
import InteractiveTooltip from './interactive-tooltip';

const icons = Quill.import('ui/icons') as Record<string, string>;
icons.undo = `<svg class="MuiSvgIcon-root jss876" focusable="false" viewBox="0 0 24 24" aria-hidden="true" role="presentation">
<path d="M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z"></path>
</svg>`;
icons.redo = `<svg class="MuiSvgIcon-root jss876" focusable="false" viewBox="0 0 24 24" aria-hidden="true" role="presentation">
<path d="M18.4 10.6C16.55 8.99 14.15 8 11.5 8c-4.65 0-8.58 3.03-9.96 7.22L3.9 16c1.05-3.19 4.05-5.5 7.6-5.5 1.95 0 3.73.72 5.12 1.88L13 16h9V7l-3.6 3.6z"></path>
</svg>`;

Quill.register({
  'blots/highlight': HighlightBlot,
});

const Block = Quill.import('blots/block') as any;
Block.tagName = 'DIV';
Quill.register(Block, true);

type Category = keyof HighlightTexts;

export interface HighlightContent {
  text: string;
  categories: string;
}

export interface HoveredHighlightState {
  element?: HTMLElement;
  isOpen: boolean;
  content?: HighlightContent;
  contentType?: TooltipContentType;
  isInteractive: boolean;
}

export interface ClickedHighlightState {
  element?: HTMLElement;
  isOpen: boolean;
  content?: Paraphrase | GrammarReplacement;
  contentType?: TooltipContentType;
  isInteractive: boolean;
}

interface Props {
  highlights: HighlightType[];
  grammarReplacements: GrammarReplacement[];
  paraphrases: Paraphrase[];
  loading: boolean;
  height?: number | string;
  placeholder?: string;

  dynamicStyles?: string;
  highlightTexts?: HighlightTexts;
  clickedChartCategory?: string | string[];
  clickedSidebarNotesCategory?: string | string[];

  variant?: 'normal' | 'outlook';
  disableToolbar?: boolean;
  disableWordCount?: boolean;

  optimizationMatch?: number | null;

  updateSuggestionsFromEditor?(idList: string[]): void;

  selectActiveSuggestion?(event: React.MouseEvent): void;

  checkIfEmpty(text: string): void;

  insertAtPosition(word: string, pos: number | number[]): () => void;

  skipSuggestion(content: any, id: string): () => void;

  ignoreSuggestion(content: any, id: string): () => void;

  handleEditorFocus?(): void;
}

const CustomQuillEditor = React.forwardRef<ReactQuill, Props>(function CustomQuillEditor(
  {
    highlights,
    grammarReplacements,
    paraphrases,
    loading,
    height,
    placeholder,
    dynamicStyles,
    highlightTexts,
    clickedChartCategory,
    clickedSidebarNotesCategory,
    variant = 'normal',
    disableToolbar,
    disableWordCount,
    optimizationMatch,
    updateSuggestionsFromEditor,
    selectActiveSuggestion,
    checkIfEmpty,
    insertAtPosition,
    skipSuggestion,
    ignoreSuggestion,
    handleEditorFocus,
  },
  ref
) {
  const { t } = useTranslation();
  const [toolbarHeight, setToolbarHeight] = useState(42);
  const { classes, cx } = useStyles({ toolbarHeight, loading, height, variant });

  const [qEditor, setQEditor] = useState<any | null>(null);

  useEffect(() => {
    if (!_.isNil(ref)) {
      const newEditor = (ref as React.MutableRefObject<ReactQuill>).current?.getEditor();

      if (!_.isNil(newEditor)) {
        setQEditor(newEditor);

        setTimeout(() => {
          const toolbar = newEditor.getModule('toolbar') as any;

          if (!_.isNil(toolbar)) {
            setToolbarHeight(toolbar.container.offsetHeight);
          }
        }, 225);
      }
    }

    return () => {
      setQEditor(null);
    };
  }, [ref]);

  const setText = useOutlookTextStore((state) => state.setText);

  const [count, setCount] = useState<{ chars: number; words: number }>({ chars: 0, words: 0 });
  const [hoverHighlightState, setHoverHighlightState] = useState<HoveredHighlightState>({
    isOpen: false,
    isInteractive: false,
  });

  const [clickedHighlightState, setClickedHighlightState] = useState<ClickedHighlightState>({
    isOpen: false,
    isInteractive: false,
  });

  const [showMatch, setShowMatch] = useState(false);

  useEffect(() => {
    if (!_.isNil(qEditor)) {
      qEditor?.root.setAttribute('spellcheck', 'false');

      const undo = document.querySelector('.ql-undo');
      if (!_.isNil(undo)) {
        undo.addEventListener('click', undoHandler, false);
      }

      const redo = document.querySelector('.ql-redo');
      if (!_.isNil(redo)) {
        redo.addEventListener('click', redoHandler, false);
      }

      document.getElementsByClassName('ql-picker-label')[0]?.setAttribute('title', t('fontText'));
      document.getElementsByClassName('ql-bold')[0]?.setAttribute('title', t('boldText'));
      document.getElementsByClassName('ql-italic')[0]?.setAttribute('title', t('italicText'));
      document.getElementsByClassName('ql-underline')[0]?.setAttribute('title', t('underlineText'));
      document.getElementsByClassName('ql-list')[0]?.setAttribute('title', t('numberedList'));
      document.getElementsByClassName('ql-list')[1]?.setAttribute('title', t('bulletList'));
      document.getElementsByClassName('ql-indent')[0]?.setAttribute('title', t('indentBackward'));
      document.getElementsByClassName('ql-indent')[1]?.setAttribute('title', t('indentForward'));
    }
  }, [qEditor]);

  function undoHandler() {
    if (!_.isNil(qEditor)) {
      return (qEditor as any).history.undo();
    }
  }

  function redoHandler() {
    if (!_.isNil(qEditor)) {
      return (qEditor as any).history.redo();
    }
  }

  useEffect(() => {
    try {
      if (!_.isNil(qEditor)) {
        _.forEach(highlights, (highlight) => {
          if (highlight.highlightPosition) {
            qEditor.formatText(highlight.highlightPosition[0], highlight.highlightPosition[1] - highlight.highlightPosition[0], 'highlight', {
              categories: highlight.categories,
              contentType: TooltipContentType.HIGHLIGHT,
            });
          }
        });
      }
    } catch (e) {
      Logger.error('Format Highlights Error: ', e);
    }
  }, [highlights]);

  useEffect(() => {
    try {
      if (!_.isNil(qEditor)) {
        _.forEach(grammarReplacements, (grammarReplacement) => {
          qEditor.formatText(
            grammarReplacement.highlightPosition[0],
            grammarReplacement.highlightPosition[1] - grammarReplacement.highlightPosition[0],
            'highlight',
            {
              suggestions: grammarReplacement,
              contentType: TooltipContentType.GRAMMAR_REPLACEMENT,
              highlightId: `${TooltipContentType.GRAMMAR_REPLACEMENT}-${grammarReplacement.ranking_index}`,
            }
          );
        });
      }
    } catch (e) {
      Logger.error('Format Grammar Replacements Error: ', e);
    }
  }, [grammarReplacements]);

  useEffect(() => {
    try {
      if (!_.isNil(qEditor)) {
        _.forEach(paraphrases, (paraphrase) => {
          qEditor.formatText(paraphrase.highlightPosition[0], paraphrase.highlightPosition[1] - paraphrase.highlightPosition[0], 'highlight', {
            suggestions: paraphrase,
            contentType: TooltipContentType.PARAPHRASE,
            highlightId: `${TooltipContentType.PARAPHRASE}-${paraphrase.ranking_index}`,
          });
        });
      }
    } catch (e) {
      Logger.error('Format Grammar Replacements Error: ', e);
    }
  }, [paraphrases]);

  const toolbarGeneral = [
    [{ header: [1, 2, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }],
    ['undo', 'redo'],
  ];

  const countWordsAndChars = (text: string) => {
    const tempText = _.cloneDeep(text);
    const replaced = tempText.replace(/\n/g, ' ');
    const charCount = replaced.length;
    const words = replaced.split(' ');
    const wordCount = words.filter((word) => word !== '').length;

    setCount({ chars: charCount, words: wordCount });
  };

  const onEditorChange = (content: any, delta: any, source: any, editor: any) => {
    if (!_.isNil(qEditor)) {
      const text = editor.getText().trim();
      countWordsAndChars(text);
      checkIfEmpty(text);

      if (variant === 'outlook') {
        setText(text);
      }

      if (!_.isNil(updateSuggestionsFromEditor)) {
        const html = editor.getHTML();
        const element = document.createElement('html');
        element.innerHTML = html;
        const elementsById = element.querySelectorAll('*[id]');
        const ids = _.map(elementsById, (item) => item.id);
        updateSuggestionsFromEditor(ids);
      }

      const retain = delta.ops[0].retain || 0;

      // We do not want to keep highlighting the things a user types even if it is in an existing highlight
      const formatBeforePos = qEditor.getFormat(retain);
      const formatAfterPos = qEditor.getFormat(retain + 1);
      if ((formatBeforePos.highlight || formatAfterPos.highlight || formatAfterPos.hedging) && _.some(delta.ops, (e) => _.has(e, 'insert'))) {
        try {
          qEditor.formatText(retain, 1, 'highlight', false, 'api');
          qEditor.formatText(retain, 1, 'hedging', false, 'api');
        } catch (e) {
          Logger.error('Error in On Change: ', e);
        }
      }
    }
  };

  useEffect(() => {
    if (loading) {
      setClickedHighlightState({
        ...clickedHighlightState,
        isOpen: false,
      });

      setHoverHighlightState({
        ...hoverHighlightState,
        isOpen: false,
      });
    }
  }, [loading]);

  const editorClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    const target = e.target as HTMLElement;
    // We check for the tagname as a workaround for Internet Explorer
    // The polyfill does not cover some other elements like <svg> etc. which breaks the outlook addin
    if (target.tagName === 'SPAN' && !loading) {
      const dataGrammarReplacements = target.dataset.grammarReplacements;

      let dataParaphrases = target.dataset.paraphrases;
      if (_.isNil(dataParaphrases) && target.parentElement) {
        dataParaphrases = target.parentElement.dataset.deletions;
      }

      if (!dataGrammarReplacements && !dataParaphrases) {
        // Close if no handled hover is detected
        return setClickedHighlightState({
          ...clickedHighlightState,
          isOpen: false,
          isInteractive: false,
        });
      }

      // Open hover and set the hovered element
      const newClickHighlightState: ClickedHighlightState = {
        ...clickedHighlightState,
      };

      const targetBlot = Quill.find(target);

      if (targetBlot) {
        newClickHighlightState.element = target;
      } else {
        newClickHighlightState.element = target;
      }

      if (dataGrammarReplacements) {
        const parsedSuggestions: GrammarReplacement = JSON.parse(dataGrammarReplacements);
        newClickHighlightState.content = parsedSuggestions;
        newClickHighlightState.contentType = TooltipContentType.GRAMMAR_REPLACEMENT;
        newClickHighlightState.isOpen = true;
        newClickHighlightState.isInteractive = true;
      }

      if (dataParaphrases) {
        const parsedParaphrases: Paraphrase = JSON.parse(dataParaphrases);
        newClickHighlightState.content = parsedParaphrases;
        newClickHighlightState.contentType = TooltipContentType.PARAPHRASE;
        newClickHighlightState.isOpen = true;
        newClickHighlightState.isInteractive = true;
      }

      setClickedHighlightState(newClickHighlightState);
    } else {
      setClickedHighlightState({
        ...clickedHighlightState,
        isOpen: false,
        isInteractive: false,
      });
    }
  };

  const editorHover = (e: React.MouseEvent<HTMLElement>) => {
    const target = e.target as HTMLElement;
    const clickedCategory = (clickedSidebarNotesCategory ? clickedSidebarNotesCategory : clickedChartCategory) as Category;
    // We check for the tagname as a workaround for Internet Explorer
    // The polyfill does not cover some other elements like <svg> etc. which breaks the outlook addin
    if (target.tagName === 'SPAN' && !hoverHighlightState.isInteractive && !loading && !_.isNil(clickedCategory)) {
      // Category and suggestion highlights should be prioritized over Hedging ones
      let dataCategories = target.dataset.categories;
      if (_.isNil(dataCategories) && target.parentElement) {
        dataCategories = target.parentElement.dataset.categories;
      }

      // Open hover and set the hovered element
      const newHoverHighlightState: HoveredHighlightState = {
        ...hoverHighlightState,
      };

      if (!dataCategories) {
        // Close if no handled hover is detected
        return setHoverHighlightState({
          ...hoverHighlightState,
          isOpen: false,
        });
      }

      const targetBlot = Quill.find(target);
      if (targetBlot) {
        newHoverHighlightState.element = target;
      }

      if (dataCategories) {
        if (clickedCategory) {
          const parsedCategories = JSON.parse(dataCategories) as Category[];
          const intersectionArray = _.intersection(clickedCategory, parsedCategories) as Category[];
          if (
            parsedCategories.includes(clickedCategory) ||
            (Array.isArray(clickedCategory) && clickedCategory.some((category) => parsedCategories.includes(category)))
          ) {
            newHoverHighlightState.isOpen = true;
            if (intersectionArray.length > 0) {
              const text = highlightTexts && highlightTexts[intersectionArray[0]] ? highlightTexts[intersectionArray[0]] : '';
              newHoverHighlightState.content = {
                text: text || '',
                categories: intersectionArray[0],
              };
              newHoverHighlightState.contentType = TooltipContentType.HIGHLIGHT;
            } else {
              const text = highlightTexts && highlightTexts[parsedCategories[0]] ? highlightTexts[parsedCategories[0]] : '';
              newHoverHighlightState.content = {
                text: text || '',
                categories: parsedCategories[0],
              };
              newHoverHighlightState.contentType = TooltipContentType.HIGHLIGHT;
            }
          }
        }
      }
      setHoverHighlightState(newHoverHighlightState);
    } else {
      if (!hoverHighlightState.isInteractive) {
        setHoverHighlightState({
          ...hoverHighlightState,
          isOpen: false,
        });
      }
    }
  };

  const handleInsertClicked = (word: string, pos: number | number[]) => {
    return () => {
      setClickedHighlightState({
        ...clickedHighlightState,
        isOpen: false,
      });

      setTimeout(() => {
        insertAtPosition(word, pos)();
      }, 300);
    };
  };

  const handleSkipClicked = (content: any, id: string) => {
    return () => {
      setClickedHighlightState({
        ...clickedHighlightState,
        isOpen: false,
      });

      setTimeout(() => {
        skipSuggestion(content, id)();
      }, 300);
    };
  };

  const handleIgnoreClicked = (content: any, id: string) => {
    return () => {
      setClickedHighlightState({
        ...clickedHighlightState,
        isOpen: false,
      });

      setTimeout(() => {
        ignoreSuggestion(content, id)();
      }, 300);
    };
  };

  /**
   * Auto optimization score
   */

  useEffect(() => {
    if (!_.isNil(optimizationMatch)) {
      setShowMatch(true);
    }

    const myTimeout = setTimeout(() => {
      setShowMatch(false);
    }, 3000);

    return () => {
      clearTimeout(myTimeout);
    };
  }, [optimizationMatch]);

  return (
    <>
      {!loading && !_.isNil(hoverHighlightState.element) ? <HighlightTooltip hoverHighlightState={hoverHighlightState} /> : null}
      {!loading && !_.isNil(clickedHighlightState.element) ? (
        <InteractiveTooltip
          clickedHighlightState={clickedHighlightState}
          insertAtPosition={handleInsertClicked}
          skipSuggestion={handleSkipClicked}
          ignoreSuggestion={handleIgnoreClicked}
        />
      ) : null}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className={classes.outerEditor}
        onClick={(event: React.MouseEvent<HTMLElement>) => {
          editorClick(event);

          if (!_.isNil(selectActiveSuggestion)) {
            selectActiveSuggestion(event);
          }
        }}
        onMouseMove={editorHover}
      >
        {loading && (
          <div className={classes.loader}>
            {showMatch && !_.isNil(optimizationMatch) ? (
              <PaiCircularProgress value={optimizationMatch * 100} />
            ) : (
              <PaiDotLoader size={variant === 'outlook' ? 'normal' : 'large'} />
            )}
          </div>
        )}
        <EditorStyles dynamicStyles={dynamicStyles} />
        <ReactQuill
          className={cx('notranslate', classes.editor)}
          ref={ref}
          readOnly={loading}
          formats={['bold', 'italic', 'size', 'script', 'underline', 'font', 'header', 'indent', 'list', 'align', 'highlight', 'background']}
          defaultValue=""
          theme="snow"
          modules={{
            toolbar: variant === 'outlook' || disableToolbar ? false : toolbarGeneral,
            history: {
              delay: 1000,
              maxStack: 100,
              userOnly: false,
            },
            clipboard: {
              matchVisual: false,
            },
          }}
          onChange={onEditorChange}
          onFocus={handleEditorFocus}
          placeholder={placeholder}
        />
        {!disableWordCount ? (
          <Typography className={classes.counter}>{`${t('characters')}: ${count.chars} / ${t('words')}: ${count.words}`}</Typography>
        ) : null}
      </div>
    </>
  );
});

const useStyles = makeStyles<{ toolbarHeight: number; loading: boolean; height?: number | string; variant?: 'normal' | 'outlook' }>()(
  (theme, { toolbarHeight, loading, height, variant }) => ({
    outerEditor: {
      height: '100%',
      width: '100%',
      position: 'relative',
    },
    editor: {
      height: `calc(100% - ${toolbarHeight}px)`,
      '& .ql-toolbar': {
        paddingLeft: '16px',
        border: 'none',
        borderBottom: `1px solid ${theme.palette.greys.light}`,
        [theme.breakpoints.down('xl')]: {
          paddingLeft: '8px',
        },
        [theme.breakpoints.down('lg')]: {
          paddingLeft: '16px',
        },
      },
      '& .ql-editor': {
        padding: variant === 'outlook' ? '16px' : '16px 24px',
        height: height,
        borderTop: variant === 'outlook' ? `1px solid ${theme.palette.greys.light}` : undefined,
        [theme.breakpoints.down('xl')]: {
          padding: variant === 'outlook' ? '16px' : '16px',
        },
        [theme.breakpoints.down('lg')]: {
          padding: variant === 'outlook' ? '16px' : '16px 24px',
        },
      },
      '& .ql-blank::before': {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 'calc(100% - 48px)',
        textAlign: 'center',
      },
      '& .ql-container': {
        border: 'none',
        borderBottom: `1px solid ${theme.palette.greys.light}`,
        opacity: !loading ? 1 : 0.33,
      },
    },
    counter: {
      position: 'absolute',
      fontSize: theme.typography.pxToRem(14),
      color: theme.palette.primary.main,
      top: '12px',
      right: '16px',
      fontWeight: 'lighter',
      [theme.breakpoints.down('xl')]: {
        right: '8px',
      },
      [theme.breakpoints.down('lg')]: {
        right: '16px',
      },
      [theme.breakpoints.down(700)]: {
        fontSize: theme.typography.pxToRem(12),
        position: 'unset',
        top: 'unset',
        right: 'unset',
        textAlign: 'right',
        padding: '8px 16px',
        borderBottom: variant === 'outlook' ? undefined : `1px solid ${theme.palette.greys.light}`,
      },
    },
    loader: {
      position: 'absolute',
      top: 'calc(50% - 50px)',
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      '& svg': {
        opacity: 1,
      },
    },
  })
);

export default CustomQuillEditor;
